import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, Pencil, Plus, Trash2 } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';
import { useToast } from '../../hooks/useToast';
import { useConfirmDialog } from '../../components/common/useConfirmDialog';
import {
  uploadTemporaryMaterial,
  fetchMaterialById,
  updateCloudMaterial,
  updateTemporaryMaterial,
} from '../../features/materials/api';
import type { TemporaryMaterial, CloudMaterial } from '../../features/materials/materialsSlice';
import { HelpTooltip } from '../../components/common/HelpTooltip';
import { MaterialsPickerModal } from '../../components/materials/MaterialsPickerModal';
import { TemporaryPreviewModal } from '../../components/materials/TemporaryPreviewModal';
import { CloudMaterialsModal } from '../../components/materials/CloudMaterialsModal';
import SoundLogoGenerateModal from '../../components/SoundLogoGenerateModal';
import {
  AudioPlayButton,
  formatAudioDuration,
  downloadAudio,
} from '../../components/common/AudioPlayButton';

interface SoundLogo {
  material_id: string;
  material_table: string;
  content_type: string | null;
  audio_url: string | null;
  file_name: string | null;
  name: string | null;
  voice_name: string | null;
  duration_seconds: number | null;
  created_at: string;
}

/**
 * ナレーター名の短縮表示 (各種ダッシュの前半部分だけ返す)。
 * ボイス一覧の name は BE がタイトルのみに分割済みだが、sound_logos.voice_name には
 * 分割前の "タイトル - 説明" が保存された既存行があるため、表示時だけここで切り落とす。
 */
function shortVoiceName(name: string): string {
  const match = name.match(/^(.+?)\s*[-–—―]\s/);
  return match ? match[1] : name;
}

interface SoundLogoSectionProps {
  brandId: string;
  brandName?: string;
  tagline?: string;
}

/**
 * ブランド詳細ページの「サウンドロゴ」セクション。
 * ブランドに紐づく音声素材（サウンドロゴ）を 1行=1音声のリストで表示する。
 * 追加方法は「AIで生成」「既存素材から関連付け」「ローカルからアップロード」の3通り。
 * 紐付けは material_brand_links (source='sound_logo') で管理される。
 */
export function SoundLogoSection({ brandId, brandName, tagline }: SoundLogoSectionProps) {
  const toast = useToast();
  const { confirm, dialog } = useConfirmDialog();
  const queryClient = useQueryClient();
  const [chooserOpen, setChooserOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [detailMaterial, setDetailMaterial] = useState<TemporaryMaterial | CloudMaterial | null>(null);
  const [detailTable, setDetailTable] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['sound-logos', brandId],
    queryFn: async () => {
      const resp = await apiClient.get<{ sound_logos: SoundLogo[] }>(`/brands/${brandId}/sound-logos`);
      return resp.data.sound_logos;
    },
    enabled: !!brandId,
  });

  const logos = data ?? [];

  // 既存素材（音声）をこのブランドのサウンドロゴとして関連付ける。
  // 個人フォルダの素材は 409(requires_move_to_shared) が返るので、同意を得て
  // 共有フォルダへ移動 (move_to_shared) してから再関連付けする。
  const attachExisting = async (materialId: string) => {
    try {
      await apiClient.post(`/brands/${brandId}/sound-logos`, { material_id: materialId });
      queryClient.invalidateQueries({ queryKey: ['sound-logos', brandId] });
      toast.success('サウンドロゴに関連付けました');
    } catch (err: unknown) {
      const resp = (err as { response?: { status?: number; data?: { error?: string; requires_move_to_shared?: boolean } } })?.response;
      if (resp?.status === 409 && resp.data?.requires_move_to_shared) {
        if (!(await confirm({ title: 'この素材は個人フォルダにあります', message: '共有フォルダに移動してサウンドロゴに追加しますか？', confirmLabel: '移動して追加', variant: 'warning' }))) {
          return;
        }
        try {
          await apiClient.post(`/brands/${brandId}/sound-logos`, { material_id: materialId, move_to_shared: true });
          queryClient.invalidateQueries({ queryKey: ['sound-logos', brandId] });
          toast.success('共有フォルダに移動してサウンドロゴに追加しました');
        } catch (err2: unknown) {
          const msg2 =
            (err2 as { response?: { data?: { error?: string } } })?.response?.data?.error ||
            (err2 instanceof Error ? err2.message : '関連付けに失敗しました');
          toast.error(msg2);
        }
        return;
      }
      const msg = resp?.data?.error || (err instanceof Error ? err.message : '関連付けに失敗しました');
      toast.error(msg);
    } finally {
      setPickerOpen(false);
    }
  };

  // ローカルから音声をアップロードして関連付ける
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // 同じファイルの再選択を許可
    if (!file) return;
    if (!file.type.startsWith('audio/')) {
      toast.error('音声ファイルを選択してください');
      return;
    }
    setUploading(true);
    try {
      const uploaded = await uploadTemporaryMaterial({ file });
      // サウンドロゴ素材は共有フォルダに統一する。アップロードは新規作成なので
      // 確認なしで共有フォルダへ昇格 (move_to_shared) する。
      await apiClient.post(`/brands/${brandId}/sound-logos`, { material_id: uploaded.id, move_to_shared: true });
      queryClient.invalidateQueries({ queryKey: ['sound-logos', brandId] });
      toast.success('音声を共有フォルダに追加しました');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (err instanceof Error ? err.message : 'アップロードに失敗しました');
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  // 素材の名前を変更する（素材テーブルに応じて PATCH を出し分け）
  const renameLogo = async (logo: SoundLogo, newName: string) => {
    try {
      if (logo.material_table === 'materials') {
        await updateCloudMaterial(logo.material_id, { name: newName || null });
      } else {
        await updateTemporaryMaterial(logo.material_id, { name: newName || null });
      }
      queryClient.invalidateQueries({ queryKey: ['sound-logos', brandId] });
      toast.success('名前を更新しました');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (err instanceof Error ? err.message : '名前の更新に失敗しました');
      toast.error(msg);
    }
  };

  // サウンドロゴから外す（紐付け解除のみ・素材は残る）
  const removeLogo = async (materialId: string) => {
    if (!(await confirm({ title: 'このサウンドロゴを外しますか？', message: '音声素材自体は削除されません。', confirmLabel: '外す' }))) return;
    try {
      await apiClient.delete(`/brands/${brandId}/sound-logos/${materialId}`);
      queryClient.invalidateQueries({ queryKey: ['sound-logos', brandId] });
      toast.success('サウンドロゴから外しました');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (err instanceof Error ? err.message : '解除に失敗しました');
      toast.error(msg);
    }
  };

  // タイトルクリック: 素材を取得して詳細モーダルを開く
  const openDetail = async (logo: SoundLogo) => {
    try {
      const material = await fetchMaterialById(logo.material_id);
      if (!material) {
        toast.error('素材が見つかりませんでした');
        return;
      }
      setDetailMaterial(material);
      setDetailTable(logo.material_table);
    } catch {
      toast.error('素材の取得に失敗しました');
    }
  };
  const closeDetail = () => {
    setDetailMaterial(null);
    setDetailTable(null);
  };

  // 追加方法の選択
  const chooseAI = () => {
    setChooserOpen(false);
    setGenerateOpen(true);
  };
  const chooseExisting = () => {
    setChooserOpen(false);
    setPickerOpen(true);
  };
  const chooseUpload = () => {
    setChooserOpen(false);
    fileInputRef.current?.click();
  };

  return (
    <>
      {dialog}
      <div className="card sound-logo-card">
        <h2 className="card-title" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <img src="/music_on_off_1.svg" alt="" aria-hidden="true" className="card-title-icon" style={{ marginRight: 0 }} />
          サウンドロゴ
          <HelpTooltip ariaLabel="サウンドロゴのヘルプ">
            ブランドの音響的な識別子となる短い音声（サウンドロゴ）を登録します。AIでブランド名などを読み上げて生成するか、手元の音声ファイルのアップロード・ライブラリの音声素材の関連付けで追加してください。登録した素材は共有フォルダに「サウンドロゴ」タグ付きで保存され、このブランドに紐付きます。
          </HelpTooltip>
        </h2>

        {isLoading ? (
          <div className="loading-stats">読み込み中...</div>
        ) : (
          <div className="sound-logo-list">
            {logos.length === 0 && <p className="brand-empty-note">サウンドロゴが登録されていません。</p>}
            {logos.map((logo) => (
              <SoundLogoRow
                key={`${logo.material_table}-${logo.material_id}`}
                logo={logo}
                onOpenDetail={() => openDetail(logo)}
                onRemove={() => removeLogo(logo.material_id)}
                onRename={(newName) => renameLogo(logo, newName)}
              />
            ))}
            <button
              type="button"
              className="brand-add-button"
              onClick={() => setChooserOpen(true)}
              disabled={uploading}
            >
              <Plus size={14} aria-hidden="true" />
              {uploading ? 'アップロード中…' : 'サウンドロゴを追加'}
            </button>
          </div>
        )}
      </div>

      {/* AI 生成モーダル */}
      {generateOpen && (
        <SoundLogoGenerateModal
          brandId={brandId}
          brandName={brandName}
          tagline={tagline}
          onClose={() => setGenerateOpen(false)}
          onGenerated={() => {
            queryClient.invalidateQueries({ queryKey: ['sound-logos', brandId] });
          }}
        />
      )}

      {pickerOpen && (
        <MaterialsPickerModal
          title="サウンドロゴにする音声を選択"
          fileTypes={['audio']}
          excludeMaterialIds={logos.map((l) => l.material_id)}
          onSelect={(_url, materialId) => {
            if (materialId) attachExisting(materialId);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {/* 素材詳細モーダル（タイトルクリックで開く）。素材テーブルに応じて出し分け。 */}
      {detailMaterial && detailTable === 'materials' && (
        <CloudMaterialsModal
          material={detailMaterial as CloudMaterial}
          onClose={closeDetail}
          onRefresh={(m) => {
            queryClient.invalidateQueries({ queryKey: ['sound-logos', brandId] });
            if (m) setDetailMaterial(m);
            else closeDetail();
          }}
        />
      )}
      {detailMaterial && detailTable === 'temporary_materials' && (
        <TemporaryPreviewModal
          material={detailMaterial as TemporaryMaterial}
          onClose={closeDetail}
          onRefresh={(m) => {
            queryClient.invalidateQueries({ queryKey: ['sound-logos', brandId] });
            if (m) setDetailMaterial(m);
            else closeDetail();
          }}
        />
      )}

      {/* ローカル音声アップロード用の隠し input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* 追加方法の選択ポップアップ */}
      {chooserOpen &&
        createPortal(
          // 誤操作で閉じないよう、オーバーレイのクリックでは閉じない (✕ ボタンのみ)
          <div className="modal-overlay modal-overlay--nested">
            <div className="modal-card modal-card--sm">
              <div className="modal-header">
                <h2 className="modal-title">サウンドロゴを追加</h2>
                <button
                  type="button"
                  className="modal-close-button"
                  onClick={() => setChooserOpen(false)}
                  aria-label="閉じる"
                >
                  ×
                </button>
              </div>
              <div className="brand-cut-chooser">
                <button type="button" className="brand-cut-chooser-option" onClick={chooseAI}>
                  <img src="/reward_stars_2.svg" alt="" aria-hidden="true" className="brand-cut-chooser-icon" />
                  <span className="brand-cut-chooser-text">
                    <span className="brand-cut-chooser-title">AIで生成</span>
                    <span className="brand-cut-chooser-desc">ブランド名などを読み上げてサウンドロゴを生成します</span>
                  </span>
                </button>
                <button type="button" className="brand-cut-chooser-option" onClick={chooseExisting}>
                  <img src="/book_search.svg" alt="" aria-hidden="true" className="brand-cut-chooser-icon" />
                  <span className="brand-cut-chooser-text">
                    <span className="brand-cut-chooser-title">既存の素材を関連付け</span>
                    <span className="brand-cut-chooser-desc">ライブラリの音声素材をこのブランドに紐付けます</span>
                  </span>
                </button>
                <button type="button" className="brand-cut-chooser-option" onClick={chooseUpload}>
                  <img src="/export_file.svg" alt="" aria-hidden="true" className="brand-cut-chooser-icon" />
                  <span className="brand-cut-chooser-text">
                    <span className="brand-cut-chooser-title">ローカルからアップロード</span>
                    <span className="brand-cut-chooser-desc">手元の音声ファイルをアップロードして追加します</span>
                  </span>
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * サウンドロゴ1件の行。並びは
 * 再生ボタン, 秒数, タイトル+鉛筆 (テキスト末尾に密着), ナレーター, DL, ごみ箱。
 * 鉛筆アイコンでその場リネーム (Enter/フォーカス外しで確定、Escでキャンセル)。
 */
function SoundLogoRow({
  logo,
  onOpenDetail,
  onRemove,
  onRename,
}: {
  logo: SoundLogo;
  onOpenDetail: () => void;
  onRemove: () => void;
  onRename: (newName: string) => void;
}) {
  const name = logo.file_name || 'サウンドロゴ';
  const [editing, setEditing] = useState(false);
  // 編集初期値は raw の name (スクリプト/曲名などのフォールバック表示値を誤って保存しないため)
  const [draftName, setDraftName] = useState(logo.name ?? '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const startEditing = () => {
    setDraftName(logo.name ?? '');
    setEditing(true);
  };
  const commit = () => {
    setEditing(false);
    const trimmed = draftName.trim();
    if (trimmed !== (logo.name ?? '')) onRename(trimmed);
  };
  const cancel = () => {
    setEditing(false);
    setDraftName(logo.name ?? '');
  };

  return (
    <div className="sound-logo-row">
      {logo.audio_url ? (
        <AudioPlayButton src={logo.audio_url} />
      ) : (
        <div className="sound-logo-audio--missing">再生不可</div>
      )}
      <span className="sound-logo-duration">{formatAudioDuration(logo.duration_seconds)}</span>
      {editing ? (
        <input
          ref={inputRef}
          type="text"
          className="sound-logo-name sound-logo-name-input"
          value={draftName}
          maxLength={200}
          placeholder="名前を入力"
          onChange={(e) => setDraftName(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') cancel();
          }}
        />
      ) : (
        /* タイトル+鉛筆をひとかたまりにして、鉛筆がテキスト末尾に密着するようにする */
        <span className="sound-logo-title-group">
          <button type="button" className="sound-logo-name" onClick={onOpenDetail} title="素材の詳細を開く">
            {name}
          </button>
          <button
            type="button"
            className="sound-logo-remove"
            onClick={startEditing}
            title="名前を変更"
            aria-label="名前を変更"
          >
            <Pencil size={14} aria-hidden="true" />
          </button>
        </span>
      )}
      {logo.voice_name && (
        <span className="sound-logo-narrator" title={`ナレーター: ${logo.voice_name}`}>
          {shortVoiceName(logo.voice_name)}
        </span>
      )}
      {logo.audio_url && (
        <button
          type="button"
          className="sound-logo-remove"
          onClick={() => downloadAudio(logo.audio_url!, `${(name || 'sound-logo').replace(/\.mp3$/i, '')}.mp3`)}
          title="ダウンロード"
          aria-label="ダウンロード"
        >
          <Download size={15} aria-hidden="true" />
        </button>
      )}
      <button
        type="button"
        className="sound-logo-remove"
        onClick={onRemove}
        title="サウンドロゴから外す"
        aria-label="サウンドロゴから外す"
      >
        <Trash2 size={15} aria-hidden="true" />
      </button>
    </div>
  );
}
